import { useEffect, useRef, useState, useCallback } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import type { CategoriaDocumento, DocumentoVinculado, SolicitudDetalle } from "@/lib/types";
import { mergeDocumentosEnDetalle } from "./use-documentos-mutations";

/** Tiempo máximo esperando la confirmación del server antes de rendirse. */
const TIMEOUT_DESPACHO_MS = 60_000;

interface PendienteInterno {
  doc: DocumentoVinculado;
  desde: number;
}

/**
 * Documentos recién subidos que el CHECKLIST PERSISTIDO aún no refleja (la
 * vinculación viaja por el outbox del backend y tarda unos segundos).
 *
 * El bug que este hook previene: el POST de subida responde OK y el archivo
 * se pinta (update optimista), pero un refetch del detalle llega ANTES de que
 * el outbox despache → la respuesta del server SIN el documento pisa el cache
 * y el archivo "desaparece"… hasta que otro poll lo trae de vuelta.
 *
 * La reconciliación por eso ocurre SOLO cuando termina un fetch real del
 * detalle (transición isFetching > 0 → 0), nunca contra el cache optimista:
 *   · pendiente presente en la respuesta → confirmado, se purga.
 *   · pendiente ausente → se RE-INYECTA al cache (el archivo no parpadea)
 *     y se sigue esperando (con timeout de cortesía por si el outbox murió).
 */
export function useDocumentosPendientes(solicitudId: string) {
  const qc = useQueryClient();
  const [pendientes, setPendientes] = useState<Map<string, PendienteInterno>>(new Map());
  const fetching = useIsFetching({ queryKey: qk.solicitud(solicitudId) });
  const prevFetching = useRef(0);

  const agregar = useCallback((doc: DocumentoVinculado) => {
    setPendientes((prev) => {
      const next = new Map(prev);
      next.set(doc.documentoId, { doc, desde: Date.now() });
      return next;
    });
  }, []);

  useEffect(() => {
    const terminoFetch = prevFetching.current > 0 && fetching === 0;
    prevFetching.current = fetching;
    if (!terminoFetch || pendientes.size === 0) return;

    const detalle = qc.getQueryData<SolicitudDetalle>(qk.solicitud(solicitudId));
    if (!detalle) return;

    const visibles = new Set(detalle.checklist.documentos.map((d) => d.documentoId));
    const next = new Map(pendientes);
    const reinyectar: DocumentoVinculado[] = [];
    let cambio = false;
    for (const [id, p] of pendientes) {
      if (visibles.has(id) || Date.now() - p.desde > TIMEOUT_DESPACHO_MS) {
        next.delete(id);
        cambio = true;
      } else {
        reinyectar.push(p.doc);
      }
    }
    if (reinyectar.length > 0) {
      qc.setQueryData<SolicitudDetalle>(
        qk.solicitud(solicitudId),
        mergeDocumentosEnDetalle(detalle, reinyectar)
      );
    }
    if (cambio) setPendientes(next);
  }, [fetching, pendientes, qc, solicitudId]);

  /** Agrupa pendientes por categoría · usado para badges en cards. */
  const porCategoria = (cat: CategoriaDocumento) => {
    const list: { id: string; nombre: string }[] = [];
    for (const [id, p] of pendientes) {
      if (p.doc.categoria === cat) list.push({ id, nombre: p.doc.nombreOriginal });
    }
    return list;
  };

  return { pendientes, agregar, porCategoria };
}
